/**
 * Every server-state hook the console uses. Pages never call `api` directly; mutations invalidate
 * the queries they affect (always including the audit log, since every admin write is audited).
 */
import { QueryClient, useInfiniteQuery, useMutation, useQuery, useQueryClient, type QueryKey } from '@tanstack/react-query';
import { useSyncExternalStore } from 'react';
import { api, isApiError, sessionStore, type PlayerAction, type Session } from './client';
import type { AdminRole, AiCostDays, AuditFilter, InsightDays, CreditsBody, DecisionBody, FlagBody, JobSearch, PushBody, RangeDays, ReportState, Segment, SimulateBody } from './types';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 15_000,
      refetchOnWindowFocus: true,
      // 4xx will not fix itself (403 = no role, 400 = bad query); retry transient failures twice.
      retry: (count, error) => !(isApiError(error) && error.isClientError) && count < 2,
    },
    mutations: { retry: false },
  },
});

// Whenever the session ends (sign-out, rejected refresh), drop every cached admin response so the
// next account never sees the previous one's data. Clearing on sign-*in* instead would orphan the
// access probe that starts the moment the new session is stored.
sessionStore.subscribe(() => {
  if (!sessionStore.get()) queryClient.clear();
});

export const keys = {
  metrics: ['metrics'] as const,
  content: ['content'] as const,
  templates: ['content', 'templates'] as const,
  retention: ['retention'] as const,
  flags: ['flags'] as const,
  appConfig: ['appConfig'] as const,
  settings: ['settings'] as const,
  audit: ['audit'] as const,
  me: ['me'] as const,
  team: ['team'] as const,
  dashboard: (range: RangeDays) => ['dashboard', range] as const,
  aiCost: (days: AiCostDays) => ['aiCost', days] as const,
  players: (q: string) => ['players', q] as const,
  player: (userId: string) => ['player', userId] as const,
};

/** Server page size of every cursor list (story, manga, jobs, audit). */
export const PAGE_SIZE = 50;

function useInvalidate() {
  const qc = useQueryClient();
  return (...list: QueryKey[]) => Promise.all([...list, keys.audit].map((queryKey) => qc.invalidateQueries({ queryKey })));
}

// ---------- session ----------

export function useSession(): Session | null {
  return useSyncExternalStore(sessionStore.subscribe, sessionStore.get, sessionStore.get);
}

export function useRequestCode() {
  return useMutation({ mutationFn: (email: string) => api.auth.requestCode(email) });
}

export function useVerifyCode() {
  return useMutation({ mutationFn: (v: { email: string; code: string }) => api.auth.verifyCode(v.email, v.code) });
}

export function useSignOut() {
  return useMutation({ mutationFn: () => api.auth.signOut() });
}

/**
 * Whether this account may use the console at all, and with which roles. Roles are only known
 * server-side, so this is a viewer-level read; a 403 means "signed in, but no admin role".
 */
export function useAdminAccess() {
  return useMe();
}

export function useMe() {
  return useQuery({ queryKey: keys.me, queryFn: ({ signal }) => api.me(signal), staleTime: 60_000 });
}

const RANK: Record<AdminRole, number> = { viewer: 0, support: 1, moderator: 2, content: 3, owner: 4 };
/** Mirrors the server's ranking (ADMIN_ROLES order): a higher role includes the lower ones. */
export function useHasRole(role: AdminRole): boolean {
  const me = useMe();
  return (me.data?.roles ?? []).some((r) => RANK[r] >= RANK[role]);
}

/** A newest-first list the server pages with `before` (the last row's timestamp). */
function useCursorList<T>(queryKey: QueryKey, fetch: (before: string | undefined, signal: AbortSignal) => Promise<T[]>, cursor: (row: T) => string, enabled = true) {
  const q = useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam, signal }) => fetch(pageParam, signal),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => (last.length >= PAGE_SIZE ? cursor(last[last.length - 1]!) : undefined),
    enabled,
  });
  return { ...q, rows: q.data?.pages.flat() ?? [] };
}

// ---------- dashboard ----------

export const METRICS_REFRESH_MS = 30_000;

export function useMetrics() {
  return useQuery({
    queryKey: keys.metrics,
    queryFn: ({ signal }) => api.metrics(signal),
    refetchInterval: METRICS_REFRESH_MS,
    refetchIntervalInBackground: false,
  });
}

export function useRetention() {
  return useQuery({ queryKey: keys.retention, queryFn: ({ signal }) => api.retention(signal), staleTime: 5 * 60_000 });
}

// ---------- moderation ----------

export function useDecideReport() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (v: { reportId: string } & DecisionBody) => api.decideReport(v.reportId, { action: v.action, note: v.note }),
    onSuccess: () => invalidate(['reports'], keys.metrics, ['users'], ['player']),
  });
}

// ---------- users ----------

export function useGrantCredits() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (v: { userId: string } & CreditsBody) => api.grantCredits(v.userId, { amount: v.amount, reason: v.reason }),
    onSuccess: (_r, v) => invalidate(['users'], keys.player(v.userId), ['dashboard']),
  });
}

// ---------- jobs ----------

export function useRetryJob() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (v: { jobId: string; reason: string }) => api.retryJob(v.jobId, v.reason),
    onSuccess: () => invalidate(['jobs'], keys.metrics),
  });
}

// ---------- content ----------

export function useContentReport() {
  return useQuery({ queryKey: keys.content, queryFn: ({ signal }) => api.content(signal) });
}

export function useTemplates() {
  return useQuery({ queryKey: keys.templates, queryFn: ({ signal }) => api.templates(signal), staleTime: 5 * 60_000 });
}

export function useSimulate() {
  return useMutation({ mutationFn: (v: { key: string } & SimulateBody) => api.simulate(v.key, { gender: v.gender, statLevel: v.statLevel, met: v.met }) });
}

// ---------- live app config ----------

export function useAppConfig() {
  return useQuery({ queryKey: keys.appConfig, queryFn: ({ signal }) => api.appConfig(signal) });
}

export function useSaveAppConfig() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (v: { config: Record<string, unknown>; note: string }) => api.saveAppConfig(v),
    onSuccess: () => invalidate(keys.appConfig),
  });
}

export function useSettings() {
  return useQuery({ queryKey: keys.settings, queryFn: ({ signal }) => api.settings(signal) });
}

export function useSaveSettings() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (v: { config: Record<string, unknown>; note: string }) => api.saveSettings(v),
    onSuccess: () => invalidate(keys.settings),
  });
}

// ---------- flags ----------

export function useFlags() {
  return useQuery({ queryKey: keys.flags, queryFn: ({ signal }) => api.flags(signal) });
}

export function useSetFlag() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (v: { key: string } & FlagBody) => api.setFlag(v.key, { enabled: v.enabled, note: v.note }),
    onSuccess: () => invalidate(keys.flags),
  });
}

// ---------- operator console ----------

export function useTeam(enabled: boolean) {
  return useQuery({ queryKey: keys.team, queryFn: ({ signal }) => api.team(signal), enabled });
}

export function useSetRoles() {
  const invalidate = useInvalidate();
  return useMutation({ mutationFn: (v: { email: string; roles: AdminRole[]; reason: string }) => api.setRoles(v), onSuccess: () => invalidate(keys.team, keys.me) });
}

export function useDashboard(range: RangeDays) {
  return useQuery({ queryKey: keys.dashboard(range), queryFn: ({ signal }) => api.dashboard(range, signal), refetchInterval: 60_000, refetchIntervalInBackground: false });
}

export function useAiCost(days: AiCostDays) {
  return useQuery({ queryKey: keys.aiCost(days), queryFn: ({ signal }) => api.aiCost(days, signal) });
}

export function useInsights(days: InsightDays) {
  return useQuery({ queryKey: ['insights', days], queryFn: ({ signal }) => api.insights(days, signal) });
}

export function useGrowth() {
  return useQuery({ queryKey: ['growth'], queryFn: ({ signal }) => api.growth(signal) });
}

export function useLaunchEmail() {
  const invalidate = useInvalidate();
  return useMutation({ mutationFn: (reason: string) => api.launchEmail(reason), onSuccess: () => invalidate(['growth']) });
}

export const MIN_PLAYER_QUERY = 2;

export function usePlayers(q: string) {
  return useQuery({ queryKey: keys.players(q), queryFn: ({ signal }) => api.players(q, signal), enabled: q.length >= MIN_PLAYER_QUERY });
}

export function usePlayer(userId: string) {
  return useQuery({ queryKey: keys.player(userId), queryFn: ({ signal }) => api.player(userId, signal) });
}

export function usePlayerAction() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (v: { userId: string; action: PlayerAction; body: Record<string, unknown> }) => api.playerAction(v.userId, v.action, v.body),
    onSuccess: (_r, v) => invalidate(keys.player(v.userId), ['players'], ['dashboard']),
  });
}

export function useStoryChapters(f: { status?: string | undefined; issues?: boolean | undefined; userId?: string | undefined; npcId?: string | undefined }) {
  return useCursorList(['story', 'chapters', f], (before, signal) => api.storyChapters(f, before, signal), (r) => r.updatedAt);
}

export function useStoryChapter(chapterId: string | null) {
  return useQuery({ queryKey: ['story', 'chapter', chapterId], queryFn: ({ signal }) => api.storyChapter(chapterId!, signal), enabled: !!chapterId });
}

export function useRetryStory() {
  const invalidate = useInvalidate();
  return useMutation({ mutationFn: (v: { chapterId: string; reason: string }) => api.retryStory(v.chapterId, v.reason), onSuccess: () => invalidate(['story'], ['jobs']) });
}

export function useStorySummary() {
  return useQuery({ queryKey: ['story', 'summary'], queryFn: ({ signal }) => api.storySummary(signal), refetchInterval: 60_000, refetchIntervalInBackground: false });
}

export function useThreads(f: { status?: string | undefined; userId?: string | undefined }) {
  return useQuery({ queryKey: ['story', 'threads', f], queryFn: ({ signal }) => api.threads(f, signal) });
}

export function useRenders(f: { tier?: string | undefined; partial?: boolean | undefined }) {
  const q = useInfiniteQuery({
    queryKey: ['manga', 'renders', f],
    queryFn: ({ pageParam, signal }) => api.renders(f, pageParam, signal),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => (last.renders.length >= PAGE_SIZE ? last.renders[last.renders.length - 1]!.createdAt : undefined),
  });
  return { ...q, rows: q.data?.pages.flatMap((p) => p.renders) ?? [], failedJobs: q.data?.pages[0]?.failedJobs ?? [], thumbs: Object.assign({}, ...(q.data?.pages.map((p) => p.thumbs) ?? [])) as Record<string, string> };
}

export function useRender(renderId: string | null) {
  return useQuery({ queryKey: ['manga', 'render', renderId], queryFn: ({ signal }) => api.render(renderId!, signal), enabled: !!renderId, staleTime: 5 * 60_000 });
}

export function useRerender() {
  const invalidate = useInvalidate();
  return useMutation({ mutationFn: (v: { chapterId: string; reuseDrawnPages: boolean; reason: string }) => api.rerender(v.chapterId, { reuseDrawnPages: v.reuseDrawnPages, reason: v.reason }), onSuccess: () => invalidate(['manga'], ['jobs']) });
}

export function useJobSearch(f: JobSearch) {
  return useCursorList(['jobs', 'search', f], (before, signal) => api.jobSearch(f, before, signal), (r) => r.updatedAt);
}

export function useJob(jobId: string | null) {
  return useQuery({ queryKey: ['jobs', 'one', jobId], queryFn: ({ signal }) => api.job(jobId!, signal), enabled: !!jobId });
}

export function useReportsWithContext(state: ReportState, reason: string | undefined) {
  return useQuery({ queryKey: ['reports', 'context', state, reason ?? 'all'], queryFn: ({ signal }) => api.reportsWithContext(state, reason, signal) });
}

export function useBroadcastPreview(segment: Segment, enabled: boolean) {
  return useQuery({ queryKey: ['notify', 'preview', segment], queryFn: ({ signal }) => api.broadcastPreview(segment, signal), enabled });
}

export function useBroadcast() {
  const invalidate = useInvalidate();
  return useMutation({ mutationFn: (v: PushBody & { segment: Segment }) => api.broadcast(v), onSuccess: () => invalidate(['notify']) });
}

export function useAuditSearch(f: AuditFilter) {
  return useCursorList(['audit', 'search', f], (before, signal) => api.auditSearch(f, before, signal), (r) => r.createdAt);
}
